"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useWindowSizeCssVar = exports.useViewportHeightStyle = exports.useStylePortal = exports.useRefSizeCssVar = exports.useClassNamePortal = exports.useAvailableHeightStyle = void 0;
var _react = require("react");
var _ahooks = require("ahooks");
var _reactUse = require("react-use");
var _lodash = require("lodash");
var _utils = require("./utils");
var _checkBrowser = require("./checkBrowser");
var _mobile = require("./mobile.utils");
function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }
function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }
function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
var getHeightStyle = function getHeightStyle(height) {
  return {
    height: height,
    minHeight: height
  };
};

// This consistently gives correct viewport height calculation across various devices and browsers.
// For example, a known issue is that 100vh on iOS browsers is not accurate due to browser chrome.
var useViewportHeightStyle = function useViewportHeightStyle() {
  var iosBrowserWithUICoveringFixedFooter = (0, _utils.isSafari)({
    mobileOnly: true
  }) || (0, _checkBrowser.chromeOnIOS)();
  var shouldUseDynamicHeight = !iosBrowserWithUICoveringFixedFooter && (0, _mobile.isMobileOrTablet)();

  // Passing null prevents the hook from causing this component to re-render. Performance 📈
  var size = (0, _ahooks.useSize)(shouldUseDynamicHeight ? document.documentElement : null);
  var height = size === null || size === void 0 ? void 0 : size.height;
  if (iosBrowserWithUICoveringFixedFooter) {
    return getHeightStyle('-webkit-fill-available');
  }
  if (shouldUseDynamicHeight) {
    return getHeightStyle("".concat(Math.min(height, window.screen.height, window.innerHeight), "px"));
  }

  // This default case works for all desktop browsers.
  return getHeightStyle('100vh');
};

/**
 * Util hook for applying class names to the element targeted by a selector.
 * @param {string} selector query selector of target element
 * @param {object} style CSS styles to apply
 */
exports.useViewportHeightStyle = useViewportHeightStyle;
var useClassNamePortal = function useClassNamePortal(selector, className) {
  var element = document.querySelector(selector);
  (0, _react.useLayoutEffect)(function () {
    var _element$classList;
    if (!element || !className) {
      return;
    }
    // Spaces are not permitted in arguments passed to classList.add
    var classList = className.split(' ');
    (_element$classList = element.classList).add.apply(_element$classList, _toConsumableArray(classList));
    return function () {
      var _element$classList2;
      return (_element$classList2 = element.classList).remove.apply(_element$classList2, _toConsumableArray(classList));
    };
  }, [!!element,
  // Add back className if element remounts
  className]);
};

/**
 * Util hook for applying styles to the element targeted by a selector.
 * @param {string} selector query selector of target element
 * @param {object} style CSS styles to apply
 */
exports.useClassNamePortal = useClassNamePortal;
var useStylePortal = function useStylePortal(selector, style) {
  (0, _react.useLayoutEffect)(function () {
    var element = document.querySelector(selector);
    if (!element || !style) {
      return;
    }
    (0, _lodash.forEach)(style, function (value, key) {
      element.style[key] = value;
    });
    return function () {
      (0, _lodash.forEach)(style, function (value, key) {
        element.style[key] = null;
      });
    };
  }, [selector, style]);
};

/**
 * Create CSS variables on the root document element.
 */
exports.useStylePortal = useStylePortal;
var useCssVar = function useCssVar(name, value) {
  (0, _react.useEffect)(function () {
    var rootEl = document.documentElement;
    if (!rootEl) {
      return;
    }
    if ((0, _lodash.isUndefined)(value)) {
      rootEl.style.removeProperty("--".concat(name));
    } else {
      rootEl.style.setProperty("--".concat(name), value);
    }
    return function () {
      rootEl.style.removeProperty("--".concat(name));
    };
  }, [value]);
};

/**
 * Observe a ref's DOM element size and create CSS vars accordingly. Introduced as a hacky fix for
 * deriving cubby height which needs to account for variable header elements / height.
 */
var useRefSizeCssVar = function useRefSizeCssVar(prefix) {
  var ref = (0, _react.useRef)();
  var size = (0, _ahooks.useSize)(ref);
  useCssVar("".concat(prefix, "-width"), "".concat(size === null || size === void 0 ? void 0 : size.width, "px"));
  useCssVar("".concat(prefix, "-height"), "".concat(size === null || size === void 0 ? void 0 : size.height, "px"));
  return ref;
};

/**
 * Observe window size and create CSS vars accordingly. Introduced as a hacky solution for 100vh
 * inaccuracies in iOS Safari.
 */
exports.useRefSizeCssVar = useRefSizeCssVar;
var useWindowSizeCssVar = function useWindowSizeCssVar() {
  var _useWindowSize = (0, _reactUse.useWindowSize)(),
    width = _useWindowSize.width,
    height = _useWindowSize.height;
  useCssVar('window-width', "".concat(width, "px"));
  useCssVar('window-height', "".concat(height, "px"));
};

// Measuring the parent element is a quick fix needed when refactoring because children use
// height: 100% (which only works if parent has a defined height). Applying overflow: hidden to the
// parent element solves the problem on desktop browsers but iOS Safari translates height: 100% to
// be the full viewport height, so this hacky fix is easiest for now.
// @TODO: refactor children to use flexbox columns instead of height: 100%
exports.useWindowSizeCssVar = useWindowSizeCssVar;
var useAvailableHeightStyle = function useAvailableHeightStyle(parentEl) {
  var size = (0, _ahooks.useSize)(parentEl);
  var availableHeight = size === null || size === void 0 ? void 0 : size.height;
  return {
    height: "".concat(availableHeight, "px")
  };
};
exports.useAvailableHeightStyle = useAvailableHeightStyle;